import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Reveal from '../components/Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [contactData, setContactData] = useState({
    address: '123 Logistics Avenue',
    city: 'Business District, City 12345',
    phone_primary: '+1 (555) 123-4567',
    phone_secondary: '+1 (555) 987-6543',
    email_primary: 'info@intqualitygroup.com',
    email_secondary: 'support@intqualitygroup.com',
    business_hours_weekday: 'Mon - Fri: 8:00 AM - 6:00 PM',
    business_hours_weekend: 'Sat: 9:00 AM - 2:00 PM',
  });

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get(`${API}/contact/contact-info`);
        setContactData(response.data);
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      }
    };
    fetchContactInfo();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: [contactData.address, contactData.city],
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: [contactData.phone_primary, contactData.phone_secondary].filter(Boolean),
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: [contactData.email_primary, contactData.email_secondary].filter(Boolean),
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [contactData.business_hours_weekday, contactData.business_hours_weekend].filter(Boolean),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-gray-50 py-20">
        <div className="pointer-events-none absolute -top-24 -right-16 w-96 h-96 bg-emerald-200/40 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 w-80 h-80 bg-red-200/25 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Get in <span className="gradient-text">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Have questions about our services? Need a quote? Our team is here to help. Reach out and let's discuss your logistics needs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Reveal key={index} delay={index * 100}>
                  <Card className="h-full border-2 hover:border-emerald-500 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 group">
                    <CardContent className="p-6 text-center space-y-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto transition-colors group-hover:bg-emerald-500">
                        <Icon className="text-emerald-600 transition-colors group-hover:text-white" size={24} />
                      </div>
                      <h3 className="font-semibold text-gray-900">{info.title}</h3>
                      <div className="space-y-1">
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-sm text-gray-600">{detail}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="grid lg:grid-cols-2 gap-12">
            <Reveal>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your logistics needs..."
                    rows={6}
                  />
                </div>
                <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto px-8 py-6 text-lg">
                  Send Message
                </Button>
              </form>
            </Reveal>

            {/* Map — geocoded from the address in settings (keyless embed) */}
            <Reveal delay={120} className="bg-gray-200 rounded-2xl overflow-hidden h-full min-h-[400px] lg:min-h-0">
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  [contactData.address, contactData.city].filter(Boolean).join(', ')
                )}&z=14&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen
                loading="lazy"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20 bg-gray-900 text-white">
        <div className="pointer-events-none absolute -top-20 -right-10 w-80 h-80 bg-emerald-500/15 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need Immediate Assistance?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Our customer support team is available 24/7 to help with urgent shipping needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${(contactData.phone_primary || '').replace(/[^+\d]/g, '')}`} className="w-full sm:w-auto">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 w-full">
                Call Now: {contactData.phone_primary}
              </Button>
            </a>
            <a href={`mailto:${contactData.email_primary}`} className="w-full sm:w-auto">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-gray-800 px-8 py-6 text-lg transition-all active:scale-95 w-full">
                Email Us
              </Button>
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;